/* eslint-disable max-lines */
import {
  createSelector,
  createSlice,
  type Middleware,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';

import 'store/configureRTK';
import store from 'store';

import type { AskKlueAppType } from 'store/api/ask-klue';

export type DashboardView = {
  newsOffset: number;
  askKlueAppType: AskKlueAppType;
  askKlueThreadId?: string;
  askKlueWizardId?: string;
  askKluePendingQuestion?: string;
  askKlueHasFilledDefaultFeedback: boolean;
  askKlueDialogCounter: number;
  askKlueShowModal: boolean;
  askKlueResearchMode: boolean;
  askKlueIsModernThread: boolean;
  askKlueSidebarOpen: boolean;
  askKlueInitialScreen: boolean;
};

export const DASHBOARD_LOCAL_STORAGE = {
  ASK_KLUE_DIALOG_COUNTER: 'dashboard-ask-klue-dialog-counter',
};

const initialState: DashboardView = {
  newsOffset: 0,
  askKlueAppType: 'chat',
  askKlueThreadId: undefined,
  askKlueWizardId: undefined,
  askKluePendingQuestion: undefined,
  askKlueHasFilledDefaultFeedback: false,
  askKlueDialogCounter: 1,
  askKlueShowModal: false,
  askKlueResearchMode: false,
  askKlueIsModernThread: false,
  askKlueSidebarOpen: false,
  askKlueInitialScreen: false,
};

const dashboardView = createSlice({
  name: 'dashboardView',
  initialState,
  reducers: {
    setNewsOffset(state, action: PayloadAction<number>) {
      state.newsOffset = action.payload;
    },
    setAskKlueAppType(state, action: PayloadAction<AskKlueAppType>) {
      state.askKlueAppType = action.payload;
    },
    setAskKlueThreadId(state, action: PayloadAction<string | undefined>) {
      state.askKlueThreadId = action.payload;
    },
    setAskKluePendingQuestion(
      state,
      action: PayloadAction<string | undefined>,
    ) {
      state.askKluePendingQuestion = action.payload;
    },
    setAskKlueWizardId(state, action: PayloadAction<string | undefined>) {
      state.askKlueWizardId = action.payload;
    },
    setAskKlueHasFilledDefaultFeedback(state, action: PayloadAction<boolean>) {
      state.askKlueHasFilledDefaultFeedback = action.payload;
    },
    setAskKlueDialogCounter(state, action: PayloadAction<number | undefined>) {
      state.askKlueDialogCounter = action.payload ?? 1;
    },
    setAskKlueShowModal(state, action: PayloadAction<boolean>) {
      state.askKlueShowModal = action.payload;
    },
    setAskKlueResearchMode(state, action: PayloadAction<boolean>) {
      state.askKlueResearchMode = action.payload;
    },
    setAskKlueIsModernThread(state, action: PayloadAction<boolean>) {
      state.askKlueIsModernThread = action.payload;
    },
    setAskKlueSidebarOpen(state, action: PayloadAction<boolean>) {
      state.askKlueSidebarOpen = action.payload;
    },
    setAskKlueInitialScreen(state, action: PayloadAction<boolean>) {
      state.askKlueInitialScreen = action.payload;
    },
  },
});

store.addModel({
  name: 'dashboardView',
  state: dashboardView.getInitialState(),
  reducers: {},
  baseReducer: dashboardView.reducer,
});

const selectSelf = createSelector(
  [(state: { dashboardView: DashboardView }) => state.dashboardView],
  (dashboardView) => dashboardView,
);

const selectNewsOffset = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.newsOffset,
);

const selectAskKlueAppType = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueAppType,
);

const selectAskKlueThreadId = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueThreadId,
);

const selectAskKluePendingQuestion = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKluePendingQuestion,
);

const selectAskKlueWizardId = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueWizardId,
);

const selectAskKlueHasFilledDefaultFeedback = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueHasFilledDefaultFeedback,
);

const selectAskKlueDialogCounter = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueDialogCounter,
);

const selectAskKlueShowModal = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueShowModal,
);

const selectAskKlueResearchMode = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueResearchMode,
);

const selectAskKlueSidebarOpen = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueSidebarOpen,
);

const selectAskKlueInitialScreen = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueInitialScreen,
);

const selectAskKlueIsModernThread = createSelector(
  [selectSelf],
  (dashboardView) => dashboardView.askKlueIsModernThread,
);

export const useNewsOffset = () =>
  useSelector<{ dashboardView: DashboardView }, number>(selectNewsOffset);

export const setNewsOffset = (offset: number) =>
  store.dispatch(dashboardView.actions.setNewsOffset(offset));

export const useAskKlueAppType = () =>
  useSelector<{ dashboardView: DashboardView }, AskKlueAppType>(
    selectAskKlueAppType,
  );

export const useAskKlueThreadId = () =>
  useSelector<{ dashboardView: DashboardView }, string | undefined>(
    selectAskKlueThreadId,
  );

export const useAskKluePendingQuestion = () =>
  useSelector<{ dashboardView: DashboardView }, string | undefined>(
    selectAskKluePendingQuestion,
  );

export const useAskKlueWizardId = () =>
  useSelector<{ dashboardView: DashboardView }, string | undefined>(
    selectAskKlueWizardId,
  );

export const useAskKlueHasFilledDefaultFeedback = () =>
  useSelector<{ dashboardView: DashboardView }, boolean>(
    selectAskKlueHasFilledDefaultFeedback,
  );

export const useAskKlueDialogCounter = () =>
  useSelector<{ dashboardView: DashboardView }, number>(
    selectAskKlueDialogCounter,
  );

export const useAskKlueShowModal = () =>
  useSelector<{ dashboardView: DashboardView }, boolean>(
    selectAskKlueShowModal,
  );

export const useAskKlueResearchMode = () =>
  useSelector<{ dashboardView: DashboardView }, boolean>(
    selectAskKlueResearchMode,
  );

export const useAskKlueSidebarOpen = () =>
  useSelector<{ dashboardView: DashboardView }, boolean>(
    selectAskKlueSidebarOpen,
  );

export const useAskKlueInitialScreen = () =>
  useSelector<{ dashboardView: DashboardView }, boolean>(
    selectAskKlueInitialScreen,
  );

export const useAskKlueIsModernThread = () =>
  useSelector<{ dashboardView: DashboardView }, boolean>(
    selectAskKlueIsModernThread,
  );

export const setAskKlueAppType = (app: AskKlueAppType) =>
  store.dispatch(dashboardView.actions.setAskKlueAppType(app));

export const setAskKlueThreadId = (threadId: string | undefined) =>
  store.dispatch(dashboardView.actions.setAskKlueThreadId(threadId));

export const setAskKluePendingQuestion = (question: string | undefined) =>
  store.dispatch(dashboardView.actions.setAskKluePendingQuestion(question));

export const setAskKlueWizardId = (wizardId: string | undefined) =>
  store.dispatch(dashboardView.actions.setAskKlueWizardId(wizardId));

export const setAskKlueHasFilledDefaultFeedback = (
  hasFilledDefaultFeedback: boolean,
) =>
  store.dispatch(
    dashboardView.actions.setAskKlueHasFilledDefaultFeedback(
      hasFilledDefaultFeedback,
    ),
  );

export const setAskKlueDialogCounter = (counter?: number) =>
  store.dispatch(dashboardView.actions.setAskKlueDialogCounter(counter));

export const setAskKlueShowModal = (showModal: boolean) => {
  return store.dispatch(dashboardView.actions.setAskKlueShowModal(showModal));
};

export const setAskKlueResearchMode = (researchMode: boolean) =>
  store.dispatch(dashboardView.actions.setAskKlueResearchMode(researchMode));

export const setAskKlueSidebarOpen = (isOpen: boolean) =>
  store.dispatch(dashboardView.actions.setAskKlueSidebarOpen(isOpen));

export const setAskKlueInitialScreen = (initialScreen: boolean) =>
  store.dispatch(dashboardView.actions.setAskKlueInitialScreen(initialScreen));

export const setAskKlueIsModernThread = (isModernThread: boolean) =>
  store.dispatch(
    dashboardView.actions.setAskKlueIsModernThread(isModernThread),
  );

const askKlueDialogCounterMiddleware: Middleware = () => (next) => (action) => {
  if (dashboardView.actions.setAskKlueDialogCounter.match(action)) {
    const value =
      parseInt(
        localStorage.getItem(DASHBOARD_LOCAL_STORAGE.ASK_KLUE_DIALOG_COUNTER) ??
          '1',
      ) + 1;

    localStorage.setItem(
      DASHBOARD_LOCAL_STORAGE.ASK_KLUE_DIALOG_COUNTER,
      value.toString(),
    );
    action.payload = value;
  }
  return next(action);
};

store.addMiddleware(askKlueDialogCounterMiddleware);
export default dashboardView;
